import React, { createContext, useContext, useState, useRef, useEffect, useCallback, ReactNode } from 'react';
import { message } from 'antd';
import { WS_BASE_URL, BASE_URL } from '../config';
import notificationService from '../services/NotificationService';

type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'reconnecting' | 'error';

export interface Message {
  room_id: string;
  created: string;
  content: string | {
    body: string;
    format?: string;
    formatted_body?: string;
    msgtype: string;
    [key: string]: any;
  };
  decrypted: boolean;
  sender: string;
  msgtype: string;
  me: boolean;
  format?: string;
  formatted_body?: string;
  event_id?: string;
  type?: string;
  fileInfo?: {
    filename: string;
    mimetype: string;
    size: number;
    h_size: string;
    pathToFile: string;
  };
}

export interface Member {
  display_name: string;
  power_level: number;
  membership: string;
}

export interface OnlineUser {
  username?: string;
  user_id?: string;
  display_name: string;
  status: 'online' | 'offline' | 'unavailable' | 'unknown';
  lastSeen?: number | null;
  connections?: number;
  membership?: string;
  power_level?: number;
  room_id?: string;
}

export interface RoomOnlineMembersData {
  room_id: string;
  internal_users: {
    online: OnlineUser[];
    offline: OnlineUser[];
    total: number;
  };
  matrix_users: {
    online: OnlineUser[];
    offline: OnlineUser[];
    unavailable: OnlineUser[];
    unknown: OnlineUser[];
    total: number;
  };
  summary: {
    internal_online: number;
    internal_total: number;
    matrix_online: number;
    matrix_total: number;
    total_online: number;
    total_users: number;
  };
}

export interface OnlineMembersData {
  rooms: { [roomId: string]: RoomOnlineMembersData };
  timestamp: number;
}

export interface Room {
  id: string;
  display_name: string;
  alias: string;
  topic: string;
  hasNewMessage?: boolean;
  messages: Message[];
  members: Member[];
}

export interface Rooms {
  [key: string]: Room;
}

interface WebSocketContextType {
  sendMessage: (message: string) => void;
  status: ConnectionStatus;
  forceReconnect: () => Promise<void>;
  setActiveRoom: (roomId: string | null) => void;
  logout: () => void;
  token: string | null;
  setToken: (token: string | null) => void;
  rooms: Rooms;
  setRooms: React.Dispatch<React.SetStateAction<Rooms>>;
  fetchRooms: () => Promise<void>;
  isHistoryLoading: boolean;
  historyError: string | null;
  isAdmin: boolean;
  fetchUsers: () => void;
  addUser: (username: string, password: string) => void;
  editUser: (username: string, newPassword: string) => void;
  deleteUser: (username: string) => void;
  addMessageListener: (listener: (data: any) => void) => void;
  removeMessageListener: (listener: (data: any) => void) => void;
  webSocket: WebSocket | null;
  fetchOnlineMembers: () => Promise<OnlineMembersData | null>;
  onlineMembers: OnlineMembersData | null;
}

interface WebSocketProviderProps {
  children: ReactNode;
}

const WebSocketContext = createContext<WebSocketContextType | null>(null);

export const useWebSocket = () => {
  return useContext(WebSocketContext) as WebSocketContextType;
};

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({ children }) => {
  const [token, setToken] = useState<string | null>(() => 
    localStorage.getItem("token")
  );
  
  const [rooms, setRooms] = useState<Rooms>({});
  const [status, setStatus] = useState<ConnectionStatus>('disconnected');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isFetchInProgress, setIsFetchInProgress] = useState(false);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [onlineMembers, setOnlineMembers] = useState<OnlineMembersData | null>(null);
  
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const connectionCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastHeartbeatResponseRef = useRef<number>(0);
  const reconnectAttemptsRef = useRef<number>(0);
  const isConnectingRef = useRef<boolean>(false);
  const lastConnectAttemptRef = useRef<number>(0);
  const activeRoomRef = useRef<string | null>(null);
  const lastFetchTimeRef = useRef<number>(0);
  
  // Объявляем refs для функций, чтобы избежать циклических зависимостей
  const connectWebSocketRef = useRef<(() => Promise<void>) | undefined>(undefined);
  const forceReconnectRef = useRef<(() => Promise<void>) | undefined>(undefined);
  
  // Добавляем ref для хранения обработчиков сообщений
  const messageListenersRef = useRef<Set<(data: any) => void>>(new Set());
  
  // Добавляем ref для функции logout, чтобы избежать циклической зависимости
  const logoutRef = useRef<(() => void) | undefined>(undefined);
  
  const maxReconnectDelay = 30000;
  const pingTimeoutMs = 15000;
  const minConnectIntervalMs = 2000;
  const minFetchIntervalMs = 10000; // Минимальный интервал между запросами истории
  
  const clearTimers = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
    
    if (connectionCheckIntervalRef.current) {
      clearInterval(connectionCheckIntervalRef.current);
      connectionCheckIntervalRef.current = null;
    }
  }, []);

  const getReconnectDelay = useCallback(() => {
    const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), maxReconnectDelay);
    reconnectAttemptsRef.current += 1;
    return delay;
  }, []);

  const resetReconnectAttempts = useCallback(() => {
    reconnectAttemptsRef.current = 0;
  }, []);

  const checkServerAvailability = useCallback(async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      
      const response = await fetch(`${BASE_URL}/ws/health`, {
        method: 'GET',
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      return response.ok;
    } catch (error) {
      console.log("Сервер недоступен:", error);
      return false;
    }
  }, []);
  
  const startConnectionChecker = useCallback(() => {
    if (connectionCheckIntervalRef.current) {
      clearInterval(connectionCheckIntervalRef.current);
      connectionCheckIntervalRef.current = null;
    }
    
    connectionCheckIntervalRef.current = setInterval(() => {
      if (!token) return;
      
      if (wsRef.current) {
        const timeSinceLastHeartbeat = Date.now() - lastHeartbeatResponseRef.current;
        if (timeSinceLastHeartbeat > pingTimeoutMs && wsRef.current.readyState === WebSocket.OPEN) {
          console.warn(`Соединение кажется мертвым. Последний ответ был ${timeSinceLastHeartbeat}ms назад`);
          wsRef.current.close(4000, "Connection timeout - no heartbeat response");
        }
      } else if (status !== 'connecting' && status !== 'reconnecting' && !isConnectingRef.current) {
        const timeSinceLastAttempt = Date.now() - lastConnectAttemptRef.current;
        if (timeSinceLastAttempt > minConnectIntervalMs * 2) {
          console.log("Нет активного соединения, пробуем подключиться");
          connectWebSocketRef.current?.();
        }
      }
    }, 20000);
    
    return () => {
      if (connectionCheckIntervalRef.current) {
        clearInterval(connectionCheckIntervalRef.current);
        connectionCheckIntervalRef.current = null;
      }
    };
  }, [token, status, minConnectIntervalMs, pingTimeoutMs]);
  
  const scheduleReconnect = useCallback(() => {
    if (!token) return;
    
    if (reconnectTimeoutRef.current) {
      console.log("Переподключение уже запланировано, пропускаем");
      return;
    }
    
    const delay = getReconnectDelay();
    console.log(`Scheduling reconnect in ${delay}ms (attempt ${reconnectAttemptsRef.current})`);
    
    if (reconnectAttemptsRef.current > 3) {
      message.warning(`Проблемы с подключением. Повторная попытка через ${Math.round(delay/1000)} сек.`, 3);
    }
    
    reconnectTimeoutRef.current = setTimeout(() => {
      console.log("Attempting to reconnect...");
      connectWebSocketRef.current?.();
    }, delay);
  }, [token, getReconnectDelay]);

  const connectWebSocket = useCallback(async () => {
    if (!token) return;
    
    if (isConnectingRef.current) {
      console.log("Подключение уже выполняется, пропускаем дублирующий запрос");
      return;
    }
    
    const timeSinceLastAttempt = Date.now() - lastConnectAttemptRef.current;
    if (timeSinceLastAttempt < minConnectIntervalMs) {
      console.log(`Слишком частые попытки подключения (${timeSinceLastAttempt}ms), пропускаем`);
      return;
    }
    
    isConnectingRef.current = true;
    lastConnectAttemptRef.current = Date.now();
    
    if (wsRef.current) {
      if (wsRef.current.readyState === WebSocket.OPEN || 
          wsRef.current.readyState === WebSocket.CONNECTING) {
        wsRef.current.close();
      }
      wsRef.current = null;
    }

    clearTimers();
    
    const isServerAvailable = await checkServerAvailability();
    if (!isServerAvailable && reconnectAttemptsRef.current > 0) {
      console.log("Сервер недоступен, откладываем переподключение");
      scheduleReconnect();
      isConnectingRef.current = false;
      return;
    }

    try {
      setStatus(reconnectAttemptsRef.current > 0 ? 'reconnecting' : 'connecting');
      
      const ws = new WebSocket(`${WS_BASE_URL}?token=${token}`);
      lastHeartbeatResponseRef.current = Date.now();
      
      const connectionTimeout = setTimeout(() => {
        if (ws.readyState !== WebSocket.OPEN) {
          console.error("WebSocket connection timeout");
          ws.close();
        }
      }, 10000);
      
      ws.onopen = () => {
        console.log("WebSocket connection established");
        setStatus('connected');
        resetReconnectAttempts();
        clearTimeout(connectionTimeout);
        
        lastHeartbeatResponseRef.current = Date.now();
        
        try {
          ws.send(JSON.stringify({ type: 'ping', initial: true }));
        } catch (e) {
          console.error("Failed to send initial ping", e);
        }
        
        heartbeatIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            try {
              ws.send(JSON.stringify({ type: 'heartbeat', timestamp: Date.now() }));
              console.log("Heartbeat sent");
              
              const timeSinceLastResponse = Date.now() - lastHeartbeatResponseRef.current;
              if (timeSinceLastResponse > pingTimeoutMs) {
                console.error(`No heartbeat response for ${timeSinceLastResponse}ms. Closing connection.`);
                ws.close(4000, "No heartbeat response");
              }
            } catch (e) {
              console.error("Failed to send heartbeat", e);
              ws.close();
            }
          } else if (ws.readyState !== WebSocket.CONNECTING) {
            clearInterval(heartbeatIntervalRef.current!);
            heartbeatIntervalRef.current = null;
            if (wsRef.current === ws) {
              wsRef.current = null;
              scheduleReconnect();
            }
          }
        }, 5000);
        
        isConnectingRef.current = false;
      };
      
      ws.onmessage = (event) => {
        try {
          lastHeartbeatResponseRef.current = Date.now();
          resetReconnectAttempts();
          
          const message = JSON.parse(event.data);
          
          // Проверяем, не является ли сообщение уведомлением об удалении текущего пользователя
          if (message.type === 'user_deleted') {
            try {
              // Извлекаем информацию о пользователе из токена
              const base64Url = token?.split('.')[1] || '';
              const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
              const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
              }).join(''));
              const payload = JSON.parse(jsonPayload);
              
              // Если удаленный пользователь - текущий пользователь, выполняем выход
              if (payload.username === message.username) {
                // Отображаем сообщение пользователю
                message.error('Ваш аккаунт был удален администратором. Выполняется выход из системы...');
                // Через короткую задержку выполняем выход, используя ref
                if (logoutRef.current) {
                  setTimeout(() => logoutRef.current?.(), 2000);
                }
              }
            } catch (error) {
              console.error('Error decoding token during user deletion check:', error);
            }
          }
          
          // Вызываем все зарегистрированные обработчики
          messageListenersRef.current.forEach(listener => {
            try {
              listener(message);
            } catch (listenerError) {
              console.error("Error in WebSocket message listener:", listenerError);
            }
          });
          
          if (message.type === 'ping') {
            ws.send(JSON.stringify({ type: 'pong', timestamp: Date.now() }));
            return;
          }
          
          if (message.type === 'heartbeat') {
            return;
          }
          
          if (message.type === 'connection_success') {
            console.log(`Соединение успешно установлено для ${message.username}`);
            return;
          }
          
          // Сервер перезапускается
          if (message.type === 'server_restart') {
            console.log('🔄 Server is restarting...');
            return;
          }
          
          // Обработка автоматических обновлений онлайн участников
          if (message.type === 'online_members_update') {
            console.log('📥 Получено обновление онлайн участников через WebSocket');
            console.log('📊 Структура данных:', {
              hasRooms: !!message.data?.rooms,
              roomCount: message.data?.rooms ? Object.keys(message.data.rooms).length : 0,
              roomIds: message.data?.rooms ? Object.keys(message.data.rooms) : []
            });
            setOnlineMembers(message.data);
            return;
          }
          
          // Обработка обновлений сообщений (для редактирований)
          if (message.type === 'message_updated') {
            setRooms(prevRooms => {
              const roomId = message.room_id;
              
              if (prevRooms[roomId] && message.event_id) {
                const existingMessages = prevRooms[roomId].messages || [];
                
                // Находим индекс существующего сообщения
                const messageIndex = existingMessages.findIndex(msg => msg.event_id === message.event_id);
                
                if (messageIndex !== -1) {
                  // Заменяем существующее сообщение обновленным
                  const updatedMessages = [...existingMessages];
                  updatedMessages[messageIndex] = { ...message, type: message.originalType || message.type };
                  
                  console.log(`📝 Обновлено сообщение ${message.event_id} в комнате ${roomId} (${message.update_reason})`);
                  
                  return {
                    ...prevRooms,
                    [roomId]: {
                      ...prevRooms[roomId],
                      messages: updatedMessages
                    }
                  };
                } else {
                  // Если сообщение не найдено, добавляем его как новое
                  console.log(`ℹ️ Сообщение ${message.event_id} не найдено в комнате ${roomId}, добавляем как новое`);
                  return {
                    ...prevRooms,
                    [roomId]: {
                      ...prevRooms[roomId],
                      messages: [...existingMessages, { ...message, type: message.originalType || message.type }]
                    }
                  };
                }
              }
              return prevRooms;
            });
            return;
          }
          
          if (message.room_id) {
            setRooms(prevRooms => {
              const roomId = message.room_id;
              
              if (prevRooms[roomId]) {
                // Добавляем сообщение в messages (только если это отображаемое сообщение)
                const shouldAddToMessages = !['m.reaction', 'm.room.redaction'].includes(message.type);
                const updatedMessages = shouldAddToMessages 
                  ? [...(prevRooms[roomId].messages || []), message]
                  : (prevRooms[roomId].messages || []);
                
                // Добавляем ВСЕ события в allMessages для обработки реакций
                const currentRoom = prevRooms[roomId] as Room & { allMessages?: Message[] };
                const updatedAllMessages = [...(currentRoom.allMessages || currentRoom.messages || []), message];
                
                // Создаем обновленную комнату с новыми сообщениями
                const updatedRoom = {
                  ...prevRooms[roomId],
                  messages: updatedMessages,
                  allMessages: updatedAllMessages,
                  hasNewMessage: message.me === false && shouldAddToMessages
                } as Room & { allMessages: Message[] };
                
                // Создаем обновленный объект комнат
                const roomsWithUpdatedMessages = {
                  ...prevRooms,
                  [roomId]: updatedRoom
                };
                
                // Теперь вызываем handleNewMessage с обновленными данными
                let finalRooms = roomsWithUpdatedMessages;
                if (message.me === false) {
                  finalRooms = notificationService.handleNewMessage(
                    roomId,
                    message.me === true,
                    roomsWithUpdatedMessages,
                    activeRoomRef.current,
                    message,
                    updatedAllMessages
                  );
                }
                
                console.log(`📨 WebSocket: Получено событие ${message.type} для комнаты ${roomId}:`, {
                  eventType: message.type,
                  eventId: message.event_id,
                  addedToMessages: shouldAddToMessages,
                  messagesCount: updatedMessages.length,
                  allMessagesCount: updatedAllMessages.length
                });
                
                return finalRooms;
              }
              return prevRooms;
            });
          }
        } catch (error) {
          console.error("Error processing WebSocket message:", error, event.data);
        }
      };
      
      ws.onerror = (error) => {
        console.error("WebSocket error:", error);
        setStatus('error');
        clearTimeout(connectionTimeout);
        isConnectingRef.current = false;

        // Check if the error is due to authentication failure (403)
        if (error instanceof ErrorEvent && error.message.includes('403')) {
          message.error('Your session has expired. Please log in again.');
          logout();
        }
      };
      
      ws.onclose = (event) => {
        console.log(`WebSocket connection closed: ${event.code} ${event.reason}`);
        setStatus('disconnected');
        clearTimeout(connectionTimeout);

        // Ошибка авторизации - выход
        if(event.code === 1008) {
          message.error('Your session has expired. Please log in again.');
          logout();
        }
        
        // Сервер перезапускается - показываем сообщение
        if(event.code === 4000) {
          message.info('Server is restarting. Reconnecting...', 3);
        }
        
        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current);
          heartbeatIntervalRef.current = null;
        }
        
        if (wsRef.current === ws) {
          wsRef.current = null;
          
          // Переподключаемся для всех случаев кроме нормального закрытия (1000)
          // и ошибки авторизации (1008)
          if (token && event.code !== 1000 && event.code !== 1008) {
            scheduleReconnect();
          }
        }
        
        isConnectingRef.current = false;
      };
      
      wsRef.current = ws;
    } catch (error) {
      console.error("Failed to create WebSocket connection:", error);
      setStatus('error');
      scheduleReconnect();
      isConnectingRef.current = false;
    }
    
    startConnectionChecker();
  }, [token, clearTimers, getReconnectDelay, resetReconnectAttempts, startConnectionChecker, checkServerAvailability, scheduleReconnect]);

  useEffect(() => {
    connectWebSocketRef.current = connectWebSocket;
  }, [connectWebSocket]);

  const setActiveRoom = useCallback((roomId: string | null) => {
    activeRoomRef.current = roomId;
  }, []);

  const fetchRooms = useCallback(async () => {
    if (!token) return;

    // Проверяем, не прошло ли слишком мало времени с последнего запроса
    const now = Date.now();
    if (now - lastFetchTimeRef.current < minFetchIntervalMs) {
      console.log(`Пропускаем fetchRooms, последний запрос был ${(now - lastFetchTimeRef.current)/1000}с назад`);
      return;
    }
    
    // Если уже идет загрузка, не начинаем новую
    if (loading || isFetchInProgress) {
      console.log('Загрузка комнат уже в процессе, пропускаем повторный запрос');
      return;
    }

    lastFetchTimeRef.current = now;
    setLoading(true);
    setError(null);
    setIsFetchInProgress(true);
    
    try {
      // Create AbortController to handle timeouts
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
      
      console.log('Fetching rooms history...');
      const response = await fetch(`${BASE_URL}/history/20`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Failed to fetch rooms history: ${response.status} ${response.statusText}`);
      }

      console.log('Parsing rooms history response...');
      // Only here we're sure the data fetch is complete
      const data = await response.json();
      console.log('Rooms history loaded successfully');
      setRooms(data); // The API already returns the data in the correct format

    } catch (error) {
      console.error('Error fetching rooms history:', error);
      setError(error instanceof Error ? error.message : 'Failed to fetch rooms history');
    } finally {
      setIsFetchInProgress(false);
      setLoading(false);
    }
  }, [token, loading, isFetchInProgress]);

  useEffect(() => {
    if (token) {
      connectWebSocketRef.current?.();
      if (Object.keys(rooms).length === 0) {
        fetchRooms();
      }
    } else {
      setStatus('disconnected');
      clearTimers();
      
      if (wsRef.current) {
        if (wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.close(1000, "User logout");
        }
        wsRef.current = null;
      }
      
      setRooms({});
    }
    
    return () => {
      // Не закрывайте соединение при размонтировании компонента, 
      // если это не выход пользователя или окончательное закрытие приложения
      clearTimers();
    };
  }, [token, clearTimers, fetchRooms]);

  const sendMessage = useCallback((message: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(message);
    }
  }, []);

  const logout = useCallback(() => {
    if (wsRef.current) {
      if (wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.close(1000, "User logout");
      }
      wsRef.current = null;
    }
    
    clearTimers();
    resetReconnectAttempts();
    
    setStatus('disconnected');
    localStorage.removeItem("token");
    setToken(null);
    setRooms({});
  }, [clearTimers, resetReconnectAttempts]);

  const forceReconnect = useCallback(async () => {
    if (isConnectingRef.current) {
      message.info('Уже выполняется подключение, пожалуйста подождите');
      return;
    }
    
    const timeSinceLastAttempt = Date.now() - lastConnectAttemptRef.current;
    if (timeSinceLastAttempt < minConnectIntervalMs) {
      message.info(`Подождите, слишком частые попытки (${Math.round(timeSinceLastAttempt/1000)}с)`);
      return;
    }
    
    const isServerAvailable = await checkServerAvailability();
    
    if (!isServerAvailable) {
      message.warning('Сервер недоступен. Повторите попытку позже.', 3);
      return;
    }
    
    if (status === 'connected' && wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      console.log("Соединение уже установлено, пропускаем переподключение");
      return;
    }
    
    clearTimers();
    resetReconnectAttempts();
    
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    lastConnectAttemptRef.current = Date.now();
    
    if (token) {
      connectWebSocketRef.current?.();
    }
  }, [status, token, clearTimers, resetReconnectAttempts, checkServerAvailability, minConnectIntervalMs]);

  useEffect(() => {
    forceReconnectRef.current = forceReconnect;
  }, [forceReconnect]);

  // Декодируем JWT-токен для определения роли пользователя
  useEffect(() => {
    if (token) {
      try {
        // Простой парсинг JWT без проверки подписи, только для чтения данных
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));

        const payload = JSON.parse(jsonPayload);
        setIsAdmin(payload.isAdmin === true);
      } catch (error) {
        console.error('Error parsing JWT token:', error);
        setIsAdmin(false);
      }
    } else {
      setIsAdmin(false);
    }
  }, [token]);

  // Методы для управления пользователями
  const fetchUsers = useCallback(() => {
    if (!token || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.error('No active WebSocket connection to fetch users');
      return;
    }
    
    wsRef.current.send(JSON.stringify({ type: 'get_users' }));
  }, [token]);

  const addUser = useCallback((username: string, password: string) => {
    if (!token || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.error('No active WebSocket connection to add user');
      return;
    }
    
    wsRef.current.send(JSON.stringify({
      type: 'add_user',
      username,
      password
    }));
  }, [token]);

  const editUser = useCallback((username: string, newPassword: string) => {
    if (!token || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.error('No active WebSocket connection to edit user');
      return;
    }
    
    wsRef.current.send(JSON.stringify({
      type: 'edit_user',
      username,
      newPassword
    }));
  }, [token]);

  const deleteUser = useCallback((username: string) => {
    if (!token || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.error('No active WebSocket connection to delete user');
      message.error('Нет активного подключения для удаления пользователя');
      return;
    }
    
    console.log('Отправка запроса на удаление пользователя:', username);
    try {
      const deleteMessage = {
        type: 'delete_user',
        username
      };
      const messageStr = JSON.stringify(deleteMessage);
      console.log('Отправляемое сообщение:', messageStr);
      
      wsRef.current.send(messageStr);
    } catch (error) {
      console.error('Ошибка при отправке запроса на удаление пользователя:', error);
      message.error('Ошибка при отправке запроса на удаление пользователя');
    }
  }, [token]);

  // Метод для добавления обработчика сообщений
  const addMessageListener = useCallback((listener: (data: any) => void) => {
    messageListenersRef.current.add(listener);
  }, []);
  
  // Метод для удаления обработчика сообщений
  const removeMessageListener = useCallback((listener: (data: any) => void) => {
    messageListenersRef.current.delete(listener);
  }, []);

  // Функция для получения онлайн участников
  const fetchOnlineMembers = useCallback(async (): Promise<OnlineMembersData | null> => {
    if (!token) {
      console.error('No token available for fetching online members');
      return null;
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
      
      console.log('Fetching online members...');
      const response = await fetch(`${BASE_URL}/online-members`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Failed to fetch online members: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Online members loaded successfully:', data);
      setOnlineMembers(data);
      return data;

    } catch (error) {
      console.error('Error fetching online members:', error);
      return null;
    }
  }, [token]);

  // Сохраняем ссылку на функцию logout
  useEffect(() => {
    logoutRef.current = logout;
  }, [logout]);

  useEffect(() => {
    const handleOnline = () => {
      console.log("Сеть восстановлена, переподключаемся...");
      forceReconnect();
    };
    
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && wsRef.current?.readyState !== WebSocket.OPEN) {
        console.log("Страница стала видимой, проверяем соединение...");
        forceReconnect();
      }
    };
    
    window.addEventListener('online', handleOnline);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [forceReconnect]);

  return (
    <WebSocketContext.Provider 
      value={{ 
        sendMessage, 
        status, 
        forceReconnect, 
        setActiveRoom, 
        logout, 
        token, 
        setToken, 
        rooms, 
        setRooms,
        fetchRooms,
        isHistoryLoading: loading || isFetchInProgress,
        historyError: error,
        isAdmin,
        fetchUsers,
        addUser,
        editUser,
        deleteUser,
        addMessageListener,
        removeMessageListener,
        webSocket: wsRef.current,
        fetchOnlineMembers,
        onlineMembers
      }}
    >
      {children}
    </WebSocketContext.Provider>
  );
}; 